/**
 * ReviewSubmitTab
 *
 * Simplified review/confirmation screen. The happy path is:
 *   1. Glance at AI summary and provider changes
 *   2. Review objectives covered
 *   3. Hit "Submit Check-In" from the top action row
 *
 * Provider change cards are shown as informational by default.
 * Accept/Edit/Reject are optional secondary actions — the operator
 * only needs to intervene if something looks wrong.
 */
import { AlertCircle } from 'lucide-react';
import type { Dispatch, SetStateAction } from 'react';

import { cn } from '@/lib/utils';
import {
  type ObjectiveState,
  type ReviewDecisionPayload,
  type TreatmentCheckInV2Status,
  useSaveReviewDecisionMutation,
  useUpdateObjectivesMutation,
} from '@/services/api/treatmentCheckInV2Api';

import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Card, CardContent } from '../../ui/card';
import { Checkbox } from '../../ui/checkbox';
import { AISummaryCard } from './AISummaryCard';
import { EvidenceList } from './EvidenceList';
import { NewProviderCard } from './NewProviderCard';
import { NextCallObjectives } from './NextCallObjectives';
import { ProviderChangeCard } from './ProviderChangeCard';
import type { AIExtractionResult, ReviewDecision } from './types';
import { UnchangedProviderCard } from './UnchangedProviderCard';

// ── Props ────────────────────────────────────────────────────────────────────

interface ReviewSubmitTabProps {
  matterId: string;
  checkInId: string;
  extractionResult: AIExtractionResult;
  generationId: string;
  operatorNotes: string;
  acceptedChanges: Record<string, ReviewDecisionPayload> | null;
  status: TreatmentCheckInV2Status;
  objectiveState?: ObjectiveState | null;
  nextCallObjectives?: string[] | null;
  finalizeError: string | null;
  staleKeys: string[];
  rejectedAiSuggestedDoneIds: string[];
  setRejectedAiSuggestedDoneIds: Dispatch<SetStateAction<string[]>>;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function toReviewDecision(
  payload: ReviewDecisionPayload | undefined,
): ReviewDecision | null {
  if (!payload) return null;
  return {
    review_key: payload.review_key,
    decision: payload.decision,
    generation_id: payload.generation_id,
    reviewed_at: payload.reviewed_at,
    approved_payload: payload.approved_payload,
    resolution: payload.resolution as ReviewDecision['resolution'],
  };
}

// ── Component ────────────────────────────────────────────────────────────────

export const ReviewSubmitTab = ({
  matterId,
  checkInId,
  extractionResult,
  generationId,
  operatorNotes,
  acceptedChanges,
  status,
  objectiveState,
  nextCallObjectives,
  finalizeError,
  staleKeys,
  rejectedAiSuggestedDoneIds,
  setRejectedAiSuggestedDoneIds,
}: ReviewSubmitTabProps) => {
  const [savePlaceholderDecision] = useSaveReviewDecisionMutation();
  const [updateObjectives, { isLoading: isUpdatingObjectives }] =
    useUpdateObjectivesMutation();

  const isFinalized = status === 'FINALIZED';

  const {
    provider_updates: providerUpdates,
    unchanged_providers: unchangedProviders,
    unmatched_providers: unmatchedProviders,
    unspecified_placeholders: unspecifiedPlaceholders,
    objectives_assessment: objectivesAssessment,
  } = extractionResult;

  const assessmentById = new Map(
    objectivesAssessment.map((assessment) => [assessment.objective_id, assessment]),
  );
  const carryForwardByObjectiveId = new Map(
    (objectiveState?.carry_forward_suggestions ?? []).map((item) => [
      item.objective_id,
      item.selected,
    ]),
  );

  const reviewObjectives =
    objectiveState?.objectives?.length
      ? [...objectiveState.objectives]
          .sort((a, b) => a.rank - b.rank)
          .map((objective) => ({
            objective,
            assessment: assessmentById.get(objective.id),
          }))
      : objectivesAssessment.map((assessment) => ({
          objective: null,
          assessment,
        }));

  const coveredCount = objectivesAssessment.filter(
    (assessment) => assessment.assessment === 'covered',
  ).length;
  const totalObjectives = reviewObjectives.length;

  const handleCarryForwardToggle = async (objectiveId: string, selected: boolean) => {
    if (!objectiveState) return;
    await updateObjectives({
      matterId,
      checkInId,
      revision: objectiveState.revision,
      updates: [{ objective_id: objectiveId, carry_forward_selected: selected }],
    });
  };

  const handleObjectiveStatusToggle = async (objectiveId: string, checked: boolean) => {
    if (!objectiveState) return;
    const objective = objectiveState.objectives.find((item) => item.id === objectiveId);
    const assessment = assessmentById.get(objectiveId);
    const hasAiSuggestedDone =
      objective?.status === 'open' && assessment?.suggested_status === 'done';

    if (!checked && hasAiSuggestedDone) {
      setRejectedAiSuggestedDoneIds((current) =>
        current.includes(objectiveId) ? current : [...current, objectiveId],
      );
      return;
    }

    if (checked) {
      setRejectedAiSuggestedDoneIds((current) =>
        current.filter((candidateId) => candidateId !== objectiveId),
      );
    }

    const nextStatus = checked ? 'done' : 'open';
    if (objective?.status === nextStatus) {
      return;
    }

    if (!checked && assessment?.suggested_status === 'done') {
      setRejectedAiSuggestedDoneIds((current) =>
        current.includes(objectiveId) ? current : [...current, objectiveId],
      );
    }

    await updateObjectives({
      matterId,
      checkInId,
      revision: objectiveState.revision,
      updates: [{ objective_id: objectiveId, status: nextStatus }],
    });
  };

  return (
    <div className="flex flex-1 flex-col overflow-y-auto">
      <div className="flex w-full flex-col gap-6 overflow-y-auto p-6">
        {finalizeError && (
          <Card className="border-destructive">
            <CardContent className="flex items-start gap-3 p-4">
              <AlertCircle className="mt-0.5 size-4 shrink-0 text-destructive" />
              <div className="space-y-1">
                <p className="text-sm font-medium text-destructive">
                  {finalizeError}
                </p>
                {staleKeys.length > 0 && (
                  <ul className="list-inside list-disc text-xs text-muted-foreground">
                    {staleKeys.map((key) => (
                      <li key={key}>{key}</li>
                    ))}
                  </ul>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid gap-6 xl:grid-cols-[minmax(0,1.25fr)_minmax(320px,0.9fr)]">
          <div className="min-w-0">
            <AISummaryCard
              summary={extractionResult.ai_summary}
              originalNotes={operatorNotes}
              enhancedNotes={extractionResult.enhanced_notes}
            />
          </div>

          <div className="flex min-w-0 flex-col gap-6">
            {totalObjectives > 0 && (
              <section className="space-y-3">
                <h3 className="text-sm font-semibold">
                  Objective Review
                  <Badge
                    variant={coveredCount === totalObjectives ? 'default' : 'secondary'}
                    className="ml-2 text-[10px]"
                  >
                    {coveredCount}/{totalObjectives}
                  </Badge>
                </h3>
                <div className="space-y-3">
                  {reviewObjectives.map(({ objective, assessment }, index) => {
                    const objectiveId =
                      objective?.id ?? assessment?.objective_id ?? `assessment-${index}`;
                    const label = objective?.label ?? assessment?.label ?? 'Objective';
                    const selectedCarryForward =
                      (objective ? carryForwardByObjectiveId.get(objective.id) : false) ?? false;
                    const isAiSuggestedDone =
                      objective?.status === 'open' &&
                      assessment?.suggested_status === 'done' &&
                      !rejectedAiSuggestedDoneIds.includes(objectiveId);
                    const isChecked = objective?.status === 'done' || isAiSuggestedDone;
                    const hasDetails = Boolean(
                      objective?.why_now ||
                        objective?.done_when ||
                        assessment?.detail ||
                        assessment?.evidence_excerpt ||
                        assessment?.agrees_with_operator != null ||
                        assessment?.confidence != null ||
                        assessment?.recommended_carry_forward,
                    );

                    return (
                      <Card key={objectiveId}>
                        <CardContent className="space-y-2.5 p-3">
                          <div className="flex items-start gap-2.5">
                            <Checkbox
                              checked={isChecked}
                              disabled={!objective || isFinalized || isUpdatingObjectives}
                              className="mt-0.5 size-4.5 rounded-[5px]"
                              onCheckedChange={(checked) => {
                                if (!objective) return;
                                handleObjectiveStatusToggle(objective.id, checked === true);
                              }}
                            />
                            <div className="min-w-0 flex-1 space-y-2">
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                  <span
                                    className={cn(
                                      'text-sm font-semibold leading-5',
                                      isChecked && 'text-muted-foreground',
                                    )}
                                  >
                                    {label}
                                  </span>
                                </div>
                                {objective && !isFinalized && (
                                  <Button
                                    variant={selectedCarryForward ? 'default' : 'outline'}
                                    size="sm"
                                    className="h-8 shrink-0 text-xs"
                                    disabled={isUpdatingObjectives}
                                    onClick={() =>
                                      handleCarryForwardToggle(
                                        objective.id,
                                        !selectedCarryForward,
                                      )
                                    }
                                  >
                                    {selectedCarryForward
                                      ? 'Added to next call'
                                      : 'Add to next call'}
                                  </Button>
                                )}
                              </div>
                              {hasDetails && (
                                <details>
                                  <summary className="cursor-pointer list-none text-[11px] font-medium text-muted-foreground hover:text-foreground">
                                    Details
                                  </summary>
                                  <div className="mt-1 space-y-1 text-[11px] leading-snug text-muted-foreground">
                                    {objective?.why_now && <p>{objective.why_now}</p>}
                                    {objective?.done_when && <p>Done when: {objective.done_when}</p>}
                                    {assessment?.detail && <p>{assessment.detail}</p>}
                                    {assessment?.evidence_excerpt && (
                                      <p>&ldquo;{assessment.evidence_excerpt}&rdquo;</p>
                                    )}
                                    {assessment?.agrees_with_operator != null && (
                                      <p>
                                        {assessment.agrees_with_operator
                                          ? 'AI agrees with the current operator selection.'
                                          : 'AI disagrees with the current operator selection.'}
                                      </p>
                                    )}
                                    {assessment?.confidence != null && (
                                      <p>
                                        AI confidence: {Math.round(assessment.confidence * 100)}%
                                      </p>
                                    )}
                                    {isAiSuggestedDone && (
                                      <p>AI would mark this objective done.</p>
                                    )}
                                    {assessment?.recommended_carry_forward && (
                                      <p>AI recommends carrying this objective forward.</p>
                                    )}
                                  </div>
                                </details>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </section>
            )}

            <NextCallObjectives
              checkInId={checkInId}
              matterId={matterId}
              manualObjectives={objectiveState?.manual_objectives ?? undefined}
              legacyObjectives={
                objectiveState == null ? (nextCallObjectives ?? undefined) : undefined
              }
              suggestedObjectives={(objectiveState?.carry_forward_suggestions ?? [])
                .filter((item) => item.selected)
                .map((item) => item.label)}
              readOnly={isFinalized}
            />
          </div>
        </div>

        <div className="space-y-6">
          {providerUpdates.length > 0 && (
            <section className="space-y-3">
              <h3 className="text-sm font-semibold">
                Provider Changes
                <Badge variant="secondary" className="ml-2 text-[10px]">
                  {providerUpdates.length}
                </Badge>
              </h3>
              <p className="text-xs font-medium text-muted-foreground">
                Review is optional — edit or remove only if something looks wrong.
              </p>
              {providerUpdates.map((item) => (
                <ProviderChangeCard
                  key={item.review_key}
                  item={item}
                  matterId={matterId}
                  checkInId={checkInId}
                  generationId={generationId}
                  existingDecision={toReviewDecision(
                    acceptedChanges?.[item.review_key],
                  )}
                />
              ))}
            </section>
          )}

          {unchangedProviders.length > 0 && (
            <section className="space-y-2">
              <details className="group">
                <summary className="flex cursor-pointer items-center gap-2 text-sm font-semibold">
                  <span className="text-muted-foreground">Providers — No Changes</span>
                  <Badge variant="outline" className="text-[10px]">
                    {unchangedProviders.length} unchanged
                  </Badge>
                </summary>
                <div className="mt-2 space-y-2">
                  {unchangedProviders.map((item) => (
                    <UnchangedProviderCard
                      key={item.provider_id}
                      item={item}
                    />
                  ))}
                </div>
              </details>
            </section>
          )}

          {unmatchedProviders.length > 0 && (
            <section className="space-y-3">
              <h3 className="text-sm font-semibold">
                New Providers
                <Badge variant="default" className="ml-2 text-[10px]">
                  {unmatchedProviders.length}
                </Badge>
              </h3>
              {unmatchedProviders.map((item) => (
                <NewProviderCard
                  key={item.review_key}
                  item={item}
                  matterId={matterId}
                  checkInId={checkInId}
                  generationId={generationId}
                  existingDecision={toReviewDecision(
                    acceptedChanges?.[item.review_key],
                  )}
                />
              ))}
            </section>
          )}

          {unspecifiedPlaceholders.length > 0 && (
            <section className="space-y-3">
              <h3 className="text-sm font-semibold">
                Vague Provider Mentions
                <Badge variant="outline" className="ml-2 text-[10px]">
                  {unspecifiedPlaceholders.length}
                </Badge>
              </h3>
              <p className="text-xs font-medium text-muted-foreground">
                These providers were mentioned but couldn&apos;t be fully identified. You can create a placeholder for follow-up.
              </p>
              {unspecifiedPlaceholders.map((item) => {
                const existingDec = acceptedChanges?.[item.review_key];
                const alreadyCreated = existingDec?.decision === 'placeholder_created';
                return (
                  <Card key={item.review_key} className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm font-medium">{item.description}</p>
                        {item.specialty && (
                          <p className="text-xs text-muted-foreground">{item.specialty}</p>
                        )}
                        {item.known_info && (
                          <p className="mt-1 text-xs text-muted-foreground">
                            Known: {item.known_info}
                          </p>
                        )}
                        <Badge variant="outline" className="mt-1 text-[10px]">
                          Tier {item.info_tier}
                        </Badge>
                      </div>
                      <div className="flex gap-2">
                        {alreadyCreated ? (
                          <Badge variant="default" className="text-[10px]">
                            Placeholder Created
                          </Badge>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={isFinalized}
                              onClick={() =>
                                savePlaceholderDecision({
                                  matterId,
                                  checkInId,
                                  review_key: item.review_key,
                                  decision: 'placeholder_created',
                                  generation_id: generationId,
                                  approved_payload: {
                                    description: item.description,
                                    specialty: item.specialty ?? '',
                                  },
                                  resolution: {
                                    resolution_type: 'created_placeholder',
                                    create_payload: {
                                      name: item.description,
                                      specialty: item.specialty ?? '',
                                    },
                                  },
                                })
                              }
                            >
                              Create Placeholder
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={isFinalized}
                              onClick={() =>
                                savePlaceholderDecision({
                                  matterId,
                                  checkInId,
                                  review_key: item.review_key,
                                  decision: 'dismissed',
                                  generation_id: generationId,
                                  approved_payload: null,
                                })
                              }
                            >
                              Dismiss
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                    {item.evidence.length > 0 && (
                      <div className="mt-2">
                        <EvidenceList evidence={item.evidence} />
                      </div>
                    )}
                  </Card>
                );
              })}
            </section>
          )}
        </div>
      </div>
    </div>
  );
};
